import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Loader2, ArrowLeft, Filter } from "lucide-react";
import { toast } from "sonner";
import { safeFetchJson } from "@/utils/safeFetch";
import EventCard from "@/components/EventCard";
import WhatsAppSupport from "@/components/WhatsAppSupport";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

const EVENT_TYPE_FILTERS = [
  { value: "all", label: "All Events" },
  { value: "workshop", label: "Workshops" },
  { value: "live_class", label: "Live Classes" }
];

export default function PublicEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState("all");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
  }, []);

  const fetchPublicEvents = async () => {
    setLoading(true);
    try {
      const typeParam = eventType !== "all" ? `?event_type=${eventType}` : "";
      const parsed = await safeFetchJson(`${API_URL}/api/events/public${typeParam}`);
      
      if (parsed.ok) {
        setEvents(parsed.data || []);
      } else {
        throw new Error(parsed.data?.detail || "Failed to load events");
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicEvents();
  }, [eventType]);

  const handleRegister = (updatedEvent) => {
    // Remove the event from public list if user registered
    setEvents(prev => prev.filter(e => e.event_id !== updatedEvent.event_id));
    toast.success("Successfully registered! Check your dashboard for the meeting link.");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
              <span className="font-bold text-xl text-gray-900">TalentMeet</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {!isLoggedIn && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>
                <Button 
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={() => navigate("/register")}
                >
                  Join Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Calendar className="w-6 h-6 text-violet-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Public Events</h1>
          </div>
          <p className="text-gray-600 mt-2">
            Discover and register for upcoming workshops, live classes, and more.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Filter by:</span>
          </div>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TYPE_FILTERS.map(filter => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            <span className="ml-3 text-gray-500">Loading events...</span>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Public Events</h3>
            <p className="text-gray-500 mb-6">
              There are no public events scheduled at the moment.
            </p>
            {!isLoggedIn && (
              <Button 
                className="bg-violet-600 hover:bg-violet-700"
                onClick={() => navigate("/register")}
              >
                Create Account to Host Events
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.map(event => (
              <EventCard
                key={event.event_id}
                event={event}
                viewType="public"
                onRegister={handleRegister}
              />
            ))}
          </div>
        )}

        {/* CTA Section */}
        {!isLoggedIn && events.length > 0 && (
          <div className="mt-12 bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-3">Want to Host Your Own Events?</h2>
            <p className="text-violet-100 mb-6 max-w-xl mx-auto">
              Join TalentMeet to create and manage workshops, auditions, and more. 
              Connect with talent from across the industry.
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => navigate("/login")}
              >
                Sign In
              </Button>
              <Button 
                className="bg-white text-violet-600 hover:bg-violet-50"
                onClick={() => navigate("/register")}
              >
                Create Free Account
              </Button>
            </div>
          </div>
        )}
      </main>

      <WhatsAppSupport />
    </div>
  );
}
